/** XeAgents.ai**/
import { useState, useEffect, createContext, useContext, useCallback } from "react";
import { COUNTRIES } from "@shared/countries";
import { X, Mail, ArrowRight, AlertCircle, Loader2, KeyRound, Eye, EyeOff } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { Link } from "wouter";
import { useTranslation } from "react-i18next";
import { AuthStorage } from "@/lib/auth-storage";
import { detectSignupCountry } from "@/lib/signup-country";
import { isWorkEmail, WORK_EMAIL_ERROR_MESSAGE } from "@shared/work-email";
import { LoginMfaStep, isMfaChallenge, type LoginMfaChallenge } from "@/components/auth/LoginMfaStep";
import { AILoadingAnimation } from "./AILoadingAnimation";
import { PlanSelectionModal } from "@/components/PlanSelectionModal";
import { withBase } from "@/lib/base-path";

interface AuthDialogContextType {
  isOpen: boolean;
  activeTab: "login" | "register";
  loginOnly: boolean;
  openAuth: (tab?: "login" | "register", loginOnly?: boolean) => void;
  closeAuth: () => void;
}

const AuthDialogContext = createContext<AuthDialogContextType | null>(null);

export function useAuthDialog() {
  const context = useContext(AuthDialogContext);
  if (!context) {
    return {
      isOpen: false,
      activeTab: "login" as const,
      loginOnly: false,
      openAuth: (tab?: "login" | "register", _loginOnly?: boolean) => {
        // Navigate to landing page with auth query param (SPA-friendly)
        const authType = tab || "login";
        window.history.pushState({}, "", `/?auth=${authType}`);
        window.dispatchEvent(new PopStateEvent('popstate'));
      },
      closeAuth: () => {},
    };
  }
  return context;
}

interface AuthDialogProviderProps {
  children: React.ReactNode;
}

export function AuthDialogProvider({ children }: AuthDialogProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"login" | "register">("login");
  const [loginOnly, setLoginOnly] = useState(false);
  const [showLoadingAnimation, setShowLoadingAnimation] = useState(false);
  const [loadingUserName, setLoadingUserName] = useState<string | undefined>();
  const [pendingRedirect, setPendingRedirect] = useState<string | null>(null);

  // Check for ?auth=login or ?auth=register query parameter
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const authParam = params.get("auth");
    if (authParam === "login" || authParam === "register") {
      setActiveTab(authParam);
      setIsOpen(true);
      // Clean up URL without reload
      const newUrl = window.location.pathname;
      window.history.replaceState({}, "", newUrl);
    }
  }, []);

  const openAuth = (tab: "login" | "register" = "login", isLoginOnly: boolean = false) => {
    setActiveTab(tab);
    setLoginOnly(isLoginOnly);
    setIsOpen(true);
  };

  const closeAuth = () => {
    setIsOpen(false);
  };

  const showLoading = useCallback((userName: string, redirectUrl: string) => {
    setIsOpen(false);
    setLoadingUserName(userName);
    setPendingRedirect(redirectUrl);
    setShowLoadingAnimation(true);
  }, []);

  const handleLoadingComplete = useCallback(() => {
    if (pendingRedirect) {
      window.location.href = pendingRedirect;
    }
  }, [pendingRedirect]);

  return (
    <AuthDialogContext.Provider value={{ isOpen, activeTab, loginOnly, openAuth, closeAuth }}>
      {children}
      <AuthDialogContent 
        isOpen={isOpen} 
        onClose={closeAuth} 
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        loginOnly={loginOnly}
        onAuthSuccess={showLoading}
      />
      <AILoadingAnimation 
        isVisible={showLoadingAnimation}
        onComplete={handleLoadingComplete}
        userName={loadingUserName}
      />
    </AuthDialogContext.Provider>
  );
}

interface AuthDialogContentProps {
  isOpen: boolean;
  onClose: () => void;
  activeTab: "login" | "register";
  setActiveTab: (tab: "login" | "register") => void;
  loginOnly?: boolean;
  onAuthSuccess?: (userName: string, redirectUrl: string) => void;
}

type AuthView = "login" | "register" | "forgot-password" | "reset-password";

function AuthDialogContent({ isOpen, onClose, activeTab, setActiveTab, loginOnly = false, onAuthSuccess }: AuthDialogContentProps) {
  const { toast } = useToast();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [authView, setAuthView] = useState<AuthView>("login");
  const [showPassword, setShowPassword] = useState(false);
  const [showPlanSelection, setShowPlanSelection] = useState(false);
  const [registeredUser, setRegisteredUser] = useState<any>(null);
  // Set when the server wants an emailed code after a correct email + password
  const [mfaChallenge, setMfaChallenge] = useState<LoginMfaChallenge | null>(null);

  const [loginForm, setLoginForm] = useState({
    email: "",
    password: "",
  });
  
  // The country decides which price list the new organization is billed on, so
  // it starts on what the visitor's browser says rather than on "US" - see
  // lib/signup-country.ts.
  const [registerForm, setRegisterForm] = useState(() => ({
    email: "",
    password: "",
    name: "",
    acceptedTerms: false,
    organizationName: "",
    country: detectSignupCountry(),
  }));
  
  const [forgotPasswordForm, setForgotPasswordForm] = useState({
    email: "",
  });
  
  const [resetPasswordForm, setResetPasswordForm] = useState({
    email: "",
    newPassword: "",
    confirmPassword: "",
  });
  
  const [signupStep, setSignupStep] = useState<"details" | "otp">("details");
  /**
   * Whether signup has to prove the address with an emailed code.
   *
   * Off unless an admin turns on feature_email_verification_required;
   * /api/auth/register reads the same setting, so the step is skipped on both
   * sides or on neither. Defaults to false while the request is in flight - a
   * server that still wants the code answers 400 and the user is told.
   */
  const [emailVerificationRequired, setEmailVerificationRequired] = useState(false);
  const [forgotPasswordStep, setForgotPasswordStep] = useState<"email" | "otp" | "new-password">("email");
  const [otpCode, setOtpCode] = useState("");
  const [otpTimer, setOtpTimer] = useState(0);
  const [canResendOtp, setCanResendOtp] = useState(false);

  useEffect(() => {
    setMfaChallenge(null);
    if (activeTab === "login") {
      setAuthView("login");
    } else {
      setAuthView("register");
    }
  }, [activeTab]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/settings/public");
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setEmailVerificationRequired(data?.email_verification_required === true);
      } catch {
        // Leave it off: the server decides, and it will say so if it disagrees.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  useEffect(() => {
    if (otpTimer > 0) {
      const timer = setTimeout(() => setOtpTimer(otpTimer - 1), 1000);
      return () => clearTimeout(timer);
    } else if (otpTimer === 0 && (signupStep === "otp" || forgotPasswordStep === "otp")) {
      setCanResendOtp(true);
    }
  }, [otpTimer, signupStep, forgotPasswordStep]);

  useEffect(() => {
    if (!isOpen) {
      setMfaChallenge(null);
      setSignupStep("details");
      setForgotPasswordStep("email");
      setOtpCode("");
      setOtpTimer(0);
      setCanResendOtp(false);
      setAuthView(activeTab === "login" ? "login" : "register");
      setShowPassword(false);
      setRegisterForm({
        email: "",
        password: "",
        name: "",
        acceptedTerms: false,
        organizationName: "",
        // Re-detected rather than remembered: the visitor may have switched
        // the pricing grid's currency since the dialog was last open.
        country: detectSignupCountry(),
      });
    }
  }, [isOpen, activeTab]);

  // Post-login handling shared by the password-only path and the email-code (MFA) path
  const completeLogin = (data: any) => {
    AuthStorage.setAuthData(data.token, data.user);

    // Handle mandatory password change (admin-created users with temp password)
    if (data.mustChangePassword) {
      toast({
        title: "Password Change Required",
        description: "Please change your temporary password to continue.",
      });
      if (onAuthSuccess) {
        onAuthSuccess(data.user.name, "/app/settings?requirePasswordChange=true");
      } else {
        onClose();
        window.location.href = withBase("/app/settings?requirePasswordChange=true");
      }
      return;
    }

    toast({
      title: "Welcome back!",
      description: `Logged in as ${data.user.name}`,
    });

    const redirectUrl = data.user.isSuperAdmin === true ? "/admin" : "/app";

    if (onAuthSuccess) {
      onAuthSuccess(data.user.name, redirectUrl);
    } else {
      onClose();
      window.location.href = redirectUrl;
    }
  };

  const reportLoginError = (error: any) => {
    toast({
      title: "Login failed",
      description: error.message,
      variant: "destructive",
    });
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(loginForm),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Login failed");
      }

      // Correct password, but a code emailed to the user is still required
      if (isMfaChallenge(data)) {
        setMfaChallenge(data);
        return;
      }

      completeLogin(data);
    } catch (error: any) {
      reportLoginError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMfaSuccess = (data: any) => {
    try {
      completeLogin(data);
    } catch (error: any) {
      // Malformed success payload - fall back to the password form rather than leave the step stuck
      setMfaChallenge(null);
      reportLoginError(error);
    }
  };

  const handleMfaBack = (message?: string) => {
    setMfaChallenge(null);
    if (message) {
      toast({
        title: t("auth.mfa.signInAgain"),
        description: message,
        variant: "destructive",
      });
    }
  };

  /**
   * Creates the account and hands off to plan selection. Shared by the plain
   * signup path and the emailed-code path, so the two cannot drift.
   * Throws on failure - both callers already report errors.
   */
  const createAccount = async () => {
    const registerResponse = await fetch("/api/auth/register", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: registerForm.email,
        password: registerForm.password,
        name: registerForm.name,
        organizationName: registerForm.organizationName,
        country: registerForm.country,
      }),
    });

    const registerData = await registerResponse.json();

    if (!registerResponse.ok) {
      throw new Error(registerData.error || "Registration failed");
    }

    AuthStorage.setAuthData(registerData.token, registerData.user);

    toast({
      title: "Account created!",
      description: `Welcome, ${registerData.user.name}`,
    });

    setRegisteredUser(registerData.user);
    onClose();
    setShowPlanSelection(true);
  };

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!registerForm.acceptedTerms) {
      toast({
        title: "Terms required",
        description: "Please accept the Terms of Service and Privacy Policy",
        variant: "destructive",
      });
      return;
    }

    if (!registerForm.organizationName.trim()) {
      toast({
        title: "Organization required",
        description: "Please enter your organization or company name",
        variant: "destructive",
      });
      return;
    }

    // The server enforces this too; catching it here saves a round trip and
    // points at the field rather than answering with a bare 400.
    if (!isWorkEmail(registerForm.email)) {
      toast({
        title: "Work email required",
        description: WORK_EMAIL_ERROR_MESSAGE,
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      // No code to send: create the account from the details already entered.
      if (!emailVerificationRequired) {
        await createAccount();
        return;
      }

      const response = await fetch("/api/auth/send-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: registerForm.email,
          name: registerForm.name,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to send verification code");
      }

      toast({
        title: "Code sent!",
        description: `Check your email at ${registerForm.email}`,
      });

      setSignupStep("otp");
      setOtpTimer(300);
      setCanResendOtp(false);
      setOtpCode("");
    } catch (error: any) {
      toast({
        title: emailVerificationRequired ? "Failed to send code" : "Sign up failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setIsLoading(true);
    try {
      const response = await fetch("/api/auth/send-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: registerForm.email,
          name: registerForm.name,
        }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || "Failed to resend verification code");
      }
      
      toast({
        title: "Code resent",
        description: "A new verification code has been sent",
      });
      
      setOtpTimer(300);
      setCanResendOtp(false);
      setOtpCode("");
    } catch (error: any) {
      toast({
        title: "Failed to resend",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const verifyResponse = await fetch("/api/auth/verify-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: registerForm.email,
          otpCode,
        }),
      });
      
      const verifyData = await verifyResponse.json();
      
      if (!verifyResponse.ok) {
        throw new Error(verifyData.error || "Invalid verification code");
      }
      
      await createAccount();
    } catch (error: any) {
      toast({
        title: "Verification failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPasswordSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await fetch("/api/auth/forgot-password/send-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: forgotPasswordForm.email,
        }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || "Failed to send verification code");
      }
      
      toast({
        title: "Code sent!",
        description: "If an account exists, check your email for the code",
      });
      
      setResetPasswordForm({ ...resetPasswordForm, email: forgotPasswordForm.email });
      setForgotPasswordStep("otp");
      setOtpTimer(300);
      setCanResendOtp(false);
      setOtpCode("");
    } catch (error: any) {
      toast({
        title: "Failed to send code",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPasswordResendOTP = async () => {
    setIsLoading(true);
    try {
      const response = await fetch("/api/auth/forgot-password/send-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: forgotPasswordForm.email,
        }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || "Failed to resend verification code");
      }
      
      toast({
        title: "Code resent",
        description: "A new verification code has been sent",
      });
      
      setOtpTimer(300);
      setCanResendOtp(false);
      setOtpCode("");
    } catch (error: any) {
      toast({
        title: "Failed to resend",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPasswordVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await fetch("/api/auth/forgot-password/verify-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: forgotPasswordForm.email,
          otpCode,
        }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || "Invalid verification code");
      }
      
      toast({
        title: "Verified!",
        description: "Now set your new password",
      });
      
      setForgotPasswordStep("new-password");
    } catch (error: any) {
      toast({
        title: "Verification failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (resetPasswordForm.newPassword !== resetPasswordForm.confirmPassword) {
      toast({
        title: "Passwords don't match",
        description: "Please make sure both passwords are the same",
        variant: "destructive",
      });
      return;
    }
    
    if (resetPasswordForm.newPassword.length < 6) {
      toast({
        title: "Password too short",
        description: "Password must be at least 6 characters",
        variant: "destructive",
      });
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await fetch("/api/auth/forgot-password/reset", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: resetPasswordForm.email,
          newPassword: resetPasswordForm.newPassword,
        }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || "Failed to reset password");
      }
      
      toast({
        title: "Password reset!",
        description: "You can now sign in with your new password",
      });
      
      setAuthView("login");
      setActiveTab("login");
      setForgotPasswordStep("email");
      setLoginForm({ email: resetPasswordForm.email, password: "" });
    } catch (error: any) {
      toast({
        title: "Reset failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getDialogTitle = () => {
    if (mfaChallenge) return t("auth.mfa.title");
    switch (authView) {
      case "login": return "Welcome back";
      case "register": return "Create your account";
      case "forgot-password": return "Reset password";
      case "reset-password": return "New password";
      default: return "Sign In";
    }
  };

  const getDialogDescription = () => {
    if (mfaChallenge) {
      return t("auth.mfa.description", { length: mfaChallenge.codeLength, email: mfaChallenge.email });
    }
    switch (authView) {
      case "login": return "Sign in to your account";
      case "register": return "Get started with AI-powered calling";
      case "forgot-password": return "Enter your email to receive a reset code";
      case "reset-password": return "Enter your new password";
      default: return "";
    }
  };

  const renderForgotPassword = () => {
    if (forgotPasswordStep === "email") {
      return (
        <form onSubmit={handleForgotPasswordSendOTP} className="space-y-4">
          <div className="text-center space-y-3 mb-6">
            <div className="flex justify-center">
              <div className="h-14 w-14 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <KeyRound className="h-7 w-7 text-amber-600 dark:text-amber-400" />
              </div>
            </div>
            <div>
              <h3 className="font-semibold text-lg">Forgot password?</h3>
              <p className="text-sm text-muted-foreground">
                Enter your email and we'll send you a reset code
              </p>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="forgot-email">Email</Label>
            <Input
              id="forgot-email"
              type="email"
              placeholder="you@company.com"
              value={forgotPasswordForm.email}
              onChange={(e) => setForgotPasswordForm({ email: e.target.value })}
              required
              className="h-11"
              data-testid="input-forgot-email"
            />
          </div>
          
          <Button
            type="submit"
            className="w-full h-11 bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:hover:bg-slate-200 text-white dark:text-slate-900"
            disabled={isLoading}
            data-testid="button-forgot-send-otp"
          >
            {isLoading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <>
                Send Reset Code
                <ArrowRight className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>
          
          <Button
            type="button"
            variant="ghost"
            className="w-full"
            onClick={() => {
              setAuthView("login");
              setActiveTab("login");
            }}
            data-testid="button-back-to-login"
          >
            Back to Sign In
          </Button>
        </form>
      );
    }
    
    if (forgotPasswordStep === "otp") {
      return (
        <form onSubmit={handleForgotPasswordVerifyOTP} className="space-y-6">
          <div className="text-center space-y-3">
            <div className="flex justify-center">
              <div className="h-14 w-14 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                <Mail className="h-7 w-7 text-slate-600 dark:text-slate-300" />
              </div>
            </div>
            <div>
              <h3 className="font-semibold text-lg">Check your email</h3>
              <p className="text-sm text-muted-foreground">
                We sent a code to <strong className="text-foreground">{forgotPasswordForm.email}</strong>
              </p>
            </div>
          </div>
          
          <div className="flex justify-center">
            <InputOTP
              maxLength={6}
              value={otpCode}
              onChange={setOtpCode}
              data-testid="input-forgot-otp"
            >
              <InputOTPGroup>
                <InputOTPSlot index={0} />
                <InputOTPSlot index={1} />
                <InputOTPSlot index={2} />
                <InputOTPSlot index={3} />
                <InputOTPSlot index={4} />
                <InputOTPSlot index={5} />
              </InputOTPGroup>
            </InputOTP>
          </div>
          
          {otpTimer > 0 && (
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <AlertCircle className="h-4 w-4" />
              <span>Code expires in {Math.floor(otpTimer / 60)}:{String(otpTimer % 60).padStart(2, "0")}</span>
            </div>
          )}
          
          <div className="space-y-3">
            <Button
              type="submit"
              className="w-full h-11 bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:hover:bg-slate-200 text-white dark:text-slate-900"
              disabled={isLoading || otpCode.length !== 6}
              data-testid="button-forgot-verify"
            >
              {isLoading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                "Verify Code"
              )}
            </Button>
            
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => setForgotPasswordStep("email")}
                disabled={isLoading}
                data-testid="button-forgot-back"
              >
                Back
              </Button>
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={handleForgotPasswordResendOTP}
                disabled={isLoading || !canResendOtp}
                data-testid="button-forgot-resend"
              >
                {canResendOtp ? "Resend Code" : `Resend in ${otpTimer}s`}
              </Button>
            </div>
          </div>
        </form>
      );
    }
    
    if (forgotPasswordStep === "new-password") {
      return (
        <form onSubmit={handleResetPassword} className="space-y-4">
          <div className="text-center space-y-3 mb-6">
            <div className="flex justify-center">
              <div className="h-14 w-14 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <KeyRound className="h-7 w-7 text-green-600 dark:text-green-400" />
              </div>
            </div>
            <div>
              <h3 className="font-semibold text-lg">Create new password</h3>
              <p className="text-sm text-muted-foreground">
                Enter your new password below
              </p>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="new-password">New Password</Label>
            <div className="relative">
              <Input
                id="new-password"
                type={showPassword ? "text" : "password"}
                value={resetPasswordForm.newPassword}
                onChange={(e) => setResetPasswordForm({ ...resetPasswordForm, newPassword: e.target.value })}
                required
                minLength={6}
                className="h-11 pr-11"
                data-testid="input-new-password"
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm-password">Confirm Password</Label>
            <div className="relative">
              <Input
                id="confirm-password"
                type={showPassword ? "text" : "password"}
                value={resetPasswordForm.confirmPassword}
                onChange={(e) => setResetPasswordForm({ ...resetPasswordForm, confirmPassword: e.target.value })}
                required
                minLength={6}
                className="h-11 pr-11"
                data-testid="input-confirm-password"
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>
          
          <Button
            type="submit"
            className="w-full h-11 bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:hover:bg-slate-200 text-white dark:text-slate-900"
            disabled={isLoading}
            data-testid="button-reset-password"
          >
            {isLoading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              "Reset Password"
            )}
          </Button>
        </form>
      );
    }
    
    return null;
  };

  return (
    <>
      <PlanSelectionModal 
        isOpen={showPlanSelection}
        onPlanSelected={(planId) => {
          // PlanSelectionModal handles all redirects internally:
          // - Free plans: redirects to /app after subscription
          // - Paid plans: redirects to /app/upgrade for payment
          // No need to do anything here
        }}
      />
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent hideCloseButton className="sm:max-w-[440px] p-0 gap-0 overflow-hidden rounded-2xl border shadow-2xl bg-background" data-testid="dialog-auth">
        <VisuallyHidden.Root>
          <DialogTitle>{getDialogTitle()}</DialogTitle>
          <DialogDescription>{getDialogDescription()}</DialogDescription>
        </VisuallyHidden.Root>
        
        <Button
          variant="ghost"
          size="icon"
          className=" w-full d-flex  justify-end  top-3 right-3 z-10 text-muted-foreground hover:text-foreground"
          onClick={onClose}
          data-testid="button-close-auth"
        >
          <X className="h-5 w-5" />
        </Button>

        {isLoading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-background/95 backdrop-blur-sm rounded-2xl flex flex-col items-center justify-center gap-4 z-50"
            data-testid="auth-loading-overlay"
          >
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            >
              <div className="w-12 h-12 border-4 border-slate-200 dark:border-slate-700 border-t-amber-500 rounded-full" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-center"
            >
              <p className="font-semibold text-foreground">Processing...</p>
              <p className="text-sm text-muted-foreground">Setting up your account</p>
            </motion.div>
          </motion.div>
        )}

        <div className="p-6 pb-4 border-b bg-muted/30">

          {authView === "login" ? (
            <div className="flex flex-col items-center text-center space-y-3">
              <img
                src="/images/branding/voiceprologo.svg"
                alt="VoicePro logo"
                className="h-14 w-auto object-contain"
                data-testid="img-auth-logo"
              />
              {/* The email-code step brings its own heading */}
              {!mfaChallenge && (
                <div>
                  <h2 className="text-xl font-bold">Welcome back</h2>
                  <p className="text-muted-foreground text-sm">
                    Sign in to manage your AI voice agents
                  </p>
                </div>
              )}
            </div>
          ) : authView === "forgot-password" ? (
            <div className="space-y-1">
              <h2 className="text-xl font-bold">Reset password</h2>
              <p className="text-muted-foreground text-sm">
                {forgotPasswordStep === "email" && "Enter your email to receive a reset code"}
                {forgotPasswordStep === "otp" && "Enter the verification code"}
                {forgotPasswordStep === "new-password" && "Create your new password"}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2.5">
                <img
                  src="/images/branding/voiceprologo.svg"
                  alt="VoicePro logo"
                  className="h-10 w-auto object-contain"
                  data-testid="img-auth-logo"
                />
              </div>
              <div>
                <h2 className="text-xl font-bold">Create your account</h2>
                <p className="text-muted-foreground text-sm">
                  Get started with AI-powered calling
                </p>
              </div>
            </div>
          )}
        </div>
        
        <div className="p-6">
          {mfaChallenge ? (
            <LoginMfaStep
              challenge={mfaChallenge}
              onSuccess={handleMfaSuccess}
              onBack={handleMfaBack}
              submitButtonClassName="h-11 bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:hover:bg-slate-200 text-white dark:text-slate-900"
            />
          ) : authView === "forgot-password" ? (
            renderForgotPassword()
          ) : loginOnly ? (
            <div className="">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="dialog-login-email">Email</Label>
                  <Input
                    id="dialog-login-email"
                    type="email"
                    placeholder="you@company.com"
                    value={loginForm.email}
                    onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                    required
                    className="h-11"
                    data-testid="input-dialog-login-email"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="dialog-login-password">Password</Label>
                    <button
                      type="button"
                      className="text-sm text-primary hover:underline"
                      onClick={() => {
                        setAuthView("forgot-password");
                        setForgotPasswordForm({ email: loginForm.email });
                      }}
                      data-testid="link-forgot-password"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <div className="relative">
                    <Input
                      id="dialog-login-password"
                      type={showPassword ? "text" : "password"}
                      value={loginForm.password}
                      onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                      required
                      className="h-11 pr-11"
                      data-testid="input-dialog-login-password"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full h-11 bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:hover:bg-slate-200 text-white dark:text-slate-900"
                  disabled={isLoading}
                  data-testid="button-dialog-login"
                >
                  {isLoading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <>
                      Sign In
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </form>
            </div>
          ) : (
            <div className="">
              <Tabs value={activeTab} onValueChange={(v) => {
                setActiveTab(v as "login" | "register");
                setAuthView(v as "login" | "register");
              }} className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="login" data-testid="tab-login">Sign In</TabsTrigger>
                  <TabsTrigger value="register" data-testid="tab-register">Create Account</TabsTrigger>
                </TabsList>
                
                <TabsContent value="login" className="mt-0">
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="dialog-login-email">Email</Label>
                      <Input
                        id="dialog-login-email"
                        type="email"
                        placeholder="you@company.com"
                        value={loginForm.email}
                        onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                        required
                        className="h-11"
                        data-testid="input-dialog-login-email"
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="dialog-login-password">Password</Label>
                        <button
                          type="button"
                          className="text-sm text-primary hover:underline"
                          onClick={() => {
                            setAuthView("forgot-password");
                            setForgotPasswordForm({ email: loginForm.email });
                          }}
                          data-testid="link-forgot-password"
                        >
                          Forgot password?
                        </button>
                      </div>
                      <div className="relative">
                        <Input
                          id="dialog-login-password"
                          type={showPassword ? "text" : "password"}
                          value={loginForm.password}
                          onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                          required
                          className="h-11 pr-11"
                          data-testid="input-dialog-login-password"
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                      </div>
                    </div>
                    <Button
                      type="submit"
                      className="w-full h-11 bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:hover:bg-slate-200 text-white dark:text-slate-900"
                      disabled={isLoading}
                      data-testid="button-dialog-login"
                    >
                      {isLoading ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <>
                          Sign In
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </>
                      )}
                    </Button>
                  </form>
                </TabsContent>
                
                <TabsContent value="register" className="mt-0">
                  {signupStep === "details" && (
                    <form onSubmit={handleSendOTP} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="dialog-register-name">Full Name</Label>
                        <Input
                          id="dialog-register-name"
                          type="text"
                          placeholder="John Doe"
                          value={registerForm.name}
                          onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
                          required
                          className="h-11"
                          data-testid="input-dialog-register-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="dialog-register-org-name">Organization Name</Label>
                        <Input
                          id="dialog-register-org-name"
                          type="text"
                          placeholder="Acme Inc."
                          value={registerForm.organizationName}
                          onChange={(e) => setRegisterForm({ ...registerForm, organizationName: e.target.value })}
                          required
                          className="h-11"
                          data-testid="input-dialog-register-org-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="dialog-register-country">Country</Label>
                        <Select
                          value={registerForm.country}
                          onValueChange={(value) => setRegisterForm({ ...registerForm, country: value })}
                        >
                          <SelectTrigger className="h-11" id="dialog-register-country" data-testid="select-dialog-register-country">
                            <SelectValue placeholder="Select country" />
                          </SelectTrigger>
                          <SelectContent>
                            {COUNTRIES.map((c) => (
                              <SelectItem key={c.value} value={c.value}>
                                {c.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="dialog-register-email">Email</Label>
                        <Input
                          id="dialog-register-email"
                          type="email"
                          placeholder="you@company.com"
                          value={registerForm.email}
                          onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                          required
                          className="h-11"
                          data-testid="input-dialog-register-email"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="dialog-register-password">Password</Label>
                        <div className="relative">
                          <Input
                            id="dialog-register-password"
                            type={showPassword ? "text" : "password"}
                            value={registerForm.password}
                            onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                            required
                            minLength={6}
                            className="h-11 pr-11"
                            data-testid="input-dialog-register-password"
                          />
                          <button
                            type="button"
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                          </button>
                        </div>
                      </div>

                      <div className="flex items-start space-x-2">
                        <Checkbox
                          id="accept-terms"
                          checked={registerForm.acceptedTerms}
                          onCheckedChange={(checked) =>
                            setRegisterForm({ ...registerForm, acceptedTerms: checked === true })
                          }
                          className="mt-0.5"
                          data-testid="checkbox-accept-terms"
                        />
                        <label
                          htmlFor="accept-terms"
                          className="text-sm text-muted-foreground leading-relaxed cursor-pointer"
                        >
                          I agree to the{" "}
                          <Link href="/terms" className="text-primary hover:underline" onClick={(e) => e.stopPropagation()}>
                            Terms of Service
                          </Link>{" "}
                          and{" "}
                          <Link href="/privacy" className="text-primary hover:underline" onClick={(e) => e.stopPropagation()}>
                            Privacy Policy
                          </Link>
                        </label>
                      </div>

                      <Button
                        type="submit"
                        className="w-full h-11 bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:hover:bg-slate-200 text-white dark:text-slate-900"
                        disabled={isLoading || !registerForm.acceptedTerms || !registerForm.organizationName.trim()}
                        data-testid="button-dialog-send-otp"
                      >
                        {isLoading ? (
                          <Loader2 className="h-5 w-5 animate-spin" />
                        ) : (
                          <>
                            Continue
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </>
                        )}
                      </Button>
                    </form>
                  )}

                  {signupStep === "otp" && (
                    <form onSubmit={handleVerifyOTP} className="space-y-6">
                      <div className="text-center space-y-3">
                        <div className="flex justify-center">
                          <div className="h-14 w-14 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                            <Mail className="h-7 w-7 text-slate-600 dark:text-slate-300" />
                          </div>
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg">Check your email</h3>
                          <p className="text-sm text-muted-foreground">
                            We sent a code to <strong className="text-foreground">{registerForm.email}</strong>
                          </p>
                        </div>
                      </div>

                      <div className="flex justify-center">
                        <InputOTP
                          maxLength={6}
                          value={otpCode}
                          onChange={setOtpCode}
                          data-testid="input-dialog-otp"
                        >
                          <InputOTPGroup>
                            <InputOTPSlot index={0} />
                            <InputOTPSlot index={1} />
                            <InputOTPSlot index={2} />
                            <InputOTPSlot index={3} />
                            <InputOTPSlot index={4} />
                            <InputOTPSlot index={5} />
                          </InputOTPGroup>
                        </InputOTP>
                      </div>

                      {otpTimer > 0 && (
                        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                          <AlertCircle className="h-4 w-4" />
                          <span>Code expires in {Math.floor(otpTimer / 60)}:{String(otpTimer % 60).padStart(2, "0")}</span>
                        </div>
                      )}

                      <div className="space-y-3">
                        <Button
                          type="submit"
                          className="w-full h-11 bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:hover:bg-slate-200 text-white dark:text-slate-900"
                          disabled={isLoading || otpCode.length !== 6}
                          data-testid="button-dialog-verify"
                        >
                          {isLoading ? (
                            <Loader2 className="h-5 w-5 animate-spin" />
                          ) : (
                            "Verify & Create Account"
                          )}
                        </Button>

                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            className="flex-1"
                            onClick={() => setSignupStep("details")}
                            disabled={isLoading}
                            data-testid="button-dialog-back"
                          >
                            Back
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            className="flex-1"
                            onClick={handleResendOTP}
                            disabled={isLoading || !canResendOtp}
                            data-testid="button-dialog-resend"
                          >
                            {canResendOtp ? "Resend Code" : `Resend in ${otpTimer}s`}
                          </Button>
                        </div>
                      </div>
                    </form>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}

export default AuthDialogProvider;
